import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Instagram, Mail, MapPin } from "lucide-react";

export default function Footer() {
  const wordmarkRef = useRef(null);
  const inView = useInView(wordmarkRef, { once: true, amount: 0.2 });

  return (
    <footer
      data-testid="footer-section"
      className="relative bg-[#060912] border-t border-white/5 pt-24 md:pt-32 pb-10 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-5">
            <span className="label-overline">HueBleu Atelier</span>
            <p className="mt-6 font-serif-display italic text-2xl md:text-3xl text-white/80 leading-snug max-w-md">
              "We don't make clothing for the season. We make it for the
              decade."
            </p>

            <div className="mt-10 space-y-4 text-sm text-white/65">
              <div className="flex items-center gap-3">
                <MapPin size={14} className="text-[#D4AF37]" />
                <span>14 Lurline St, Katoomba NSW 2780, Australia</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={14} className="text-[#D4AF37]" />
                <a
                  href="mailto:atelier@huebleu.com"
                  className="gold-link"
                  data-testid="footer-email"
                >
                  atelier@huebleu.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Instagram size={14} className="text-[#D4AF37]" />
                <a
                  href="#"
                  className="gold-link"
                  data-testid="footer-instagram"
                >
                  @huebleu.studio
                </a>
              </div>
            </div>
          </div>

          <div className="col-span-6 md:col-span-2 md:col-start-7">
            <h5 className="label-overline">Shop</h5>
            <ul className="mt-5 space-y-3 text-sm">
              {["Hoods", "Crews", "Sweaters", "Archive", "Gift cards"].map(
                (l) => (
                  <li key={l}>
                    <a href="#" className="gold-link">
                      {l}
                    </a>
                  </li>
                ),
              )}
            </ul>
          </div>

          <div className="col-span-6 md:col-span-2">
            <h5 className="label-overline">House</h5>
            <ul className="mt-5 space-y-3 text-sm">
              {["Philosophy", "Atelier", "Journal", "Care guide", "Press"].map(
                (l) => (
                  <li key={l}>
                    <a href="#" className="gold-link">
                      {l}
                    </a>
                  </li>
                ),
              )}
            </ul>
          </div>

          <div className="col-span-12 md:col-span-2">
            <h5 className="label-overline">Service</h5>
            <ul className="mt-5 space-y-3 text-sm">
              {["Shipping", "Returns", "Mending", "Contact", "FAQ"].map((l) => (
                <li key={l}>
                  <a href="#" className="gold-link">
                    {l}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Massive logo wordmark — CSS recreation of HUEBLEU plate logo */}
        <motion.div
          ref={wordmarkRef}
          initial={{ opacity: 0, y: 60 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
          transition={{ duration: 1.2 }}
          className="mt-24 md:mt-32 flex justify-center select-none overflow-hidden"
          data-testid="footer-wordmark"
          aria-label="HueBleu"
        >
          <div
            className="flex items-center"
            style={{
              fontFamily: "Manrope, sans-serif",
              fontWeight: 800,
              letterSpacing: "0.16em",
              fontSize: "clamp(56px, 11vw, 200px)",
              lineHeight: 1,
            }}
          >
            {/* HUE — sits on a gold plate that reveals left → right */}
            <span
              className="relative inline-flex items-center justify-center px-[0.18em] py-[0.05em] overflow-hidden"
            >
              {/* Gold plate revealer — driven by parent inView for reliability */}
              <motion.span
                aria-hidden
                initial={{ scaleX: 0 }}
                animate={inView ? { scaleX: 1 } : { scaleX: 0 }}
                transition={{
                  duration: 1.4,
                  ease: [0.77, 0, 0.18, 1],
                  delay: 0.5,
                }}
                className="absolute inset-0 origin-left"
                style={{ background: "#D4AF37" }}
              />
              {/* HUE text — midnight, blends with bg until plate slides over */}
              <span
                className="relative"
                style={{ color: "#0a0f1d" }}
              >
                HUE
              </span>
            </span>

            {/* BLEU — always gold */}
            <span
              className="ml-[0.06em]"
              style={{ color: "#D4AF37" }}
            >
              BLEU
            </span>
          </div>
        </motion.div>

        <div className="mt-20 pt-8 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-[11px] tracking-[0.25em] uppercase text-white/40">
            © 2026 HueBleu Pty Ltd. Made in the Blue Mountains.
          </div>
          <div className="flex gap-6 text-[11px] tracking-[0.25em] uppercase text-white/40">
            <a href="#" className="gold-link">
              Privacy
            </a>
            <a href="#" className="gold-link">
              Terms
            </a>
            <a href="#" className="gold-link">
              Sustainability
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
